/**
 * Abrechnung-Test — Werkstatt-Phase 4 Inspector.
 *
 * Single-Source: liest `card.data.calculation` (MonthResult-Shape, produziert
 * von worker/src/calc-abrechnung.ts via worker/handlers/calculate.ts).
 *
 * KEINE client-side Recompute mehr — was hier angezeigt wird IST das, was
 * Production gerechnet hat. Wenn man die Werte ändern will, ändert man die
 * Calc-Logik im Worker (worker/src/calc-abrechnung.ts).
 */
import { useEffect, useState } from "react";
import { api, type Card, type Employee, type Timesheet } from "../lib/api";
import { useClientContext } from "../lib/ClientContext";
import { PipelineStepList, type PipelineStep } from "./SettingsTestOcr";
import type { MonthResult, DayResult } from "../lib/calc-abrechnung";
import { stepUuid } from "../lib/stepUuid";

export function AbrechnungTest() {
  const { selectedClientId: clientId } = useClientContext();
  const [cards, setCards] = useState<Card[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [cardId, setCardId] = useState<string>("");
  const [timesheet, setTimesheet] = useState<Timesheet | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    const month = new Date().toISOString().slice(0, 7);
    Promise.all([
      api.cards.list(month, "", clientId, "timesheet"),
      api.employees.list(clientId),
    ]).then(([cr, emps]) => {
      setCards(cr.cards.filter(c => c.kind === "timesheet"));
      setEmployees(emps);
    }).catch(e => setLoadErr(e.message));
  }, [clientId]);

  useEffect(() => {
    if (!cardId) { setTimesheet(null); return; }
    setBusy(true);
    api.timesheets.get(cardId)
      .then(setTimesheet)
      .catch(e => setLoadErr(e.message))
      .finally(() => setBusy(false));
  }, [cardId]);

  const selectedCard = cards.find(c => c.id === cardId) || null;
  const selectedEmp = selectedCard?.employee_id
    ? employees.find(e => e.id === selectedCard.employee_id) || null
    : null;
  const schedule = selectedEmp?.work_schedule ?? null;

  // PRODUCTION DATA — read from card.data.calculation (worker output)
  const prodCalc: MonthResult | null = (timesheet?.data as any)?.calculation ?? null;
  const kvSnapshot = (timesheet as any)?.kv_snapshot ?? null;

  const steps: PipelineStep[] = [
    {
      num: 1,
      title: "Karte + Periode auswählen",
      description:
        "Auswahl einer Karte in beliebigem State. Mitarbeiter via Header-OCR + Levenshtein auto-resolved.",
      status: selectedCard ? "done" : "pending",
      detail: selectedCard
        ? `${selectedCard.month} · ${selectedEmp ? selectedEmp.last_name + ", " + selectedEmp.first_name : "(unzugeordnet)"} · ${selectedCard.state}`
        : "Keine Karte ausgewählt",
    },
    {
      num: 2,
      title: "Soll-Schedule des Mitarbeiters",
      description:
        "employee.work_schedule definiert pro Wochentag die Soll-Stunden. "
        + "pattern=weekly → week_a für alle Wochen. pattern=biweekly → ISO-KW alterniert (ungerade=A, gerade=B).",
      status: schedule ? "done" : selectedEmp ? "error" : "pending",
      detail: schedule ? scheduleDetail(schedule)
        : selectedEmp ? "⚠ Kein Schedule — Abrechnung nicht möglich" : "(wartet)",
    },
    {
      num: 3,
      title: "KV-Snapshot (zum Stichtag)",
      description:
        "Worker snapshottet KV zum 1. des Monats. pause_minutes_after_6h fließt in Hinweise (NICHT in stille Subtraktion).",
      status: kvSnapshot ? "done" : prodCalc ? "error" : "pending",
      detail: kvSnapshot
        ? `${kvSnapshot.name} · Pflichtpause ${kvSnapshot.pause_minutes_after_6h}min nach 6h · Wochennorm ${kvSnapshot.normal_hours_per_week}h`
        : prodCalc ? "Kein kv_snapshot persistiert" : "(wartet)",
    },
    {
      num: 4,
      title: "Pro-Tag-Berechnung (Production-Output)",
      description:
        "Worker hat ist_h, soll_h, diff_h pro Tag berechnet. Status-Codes K/U/F → ist=soll. "
        + "ZA → ist=0 (Master-System trackt Saldo). Pflichtpause/Sequenz/Tageshöchst sind HINWEISE.",
      status: prodCalc ? "done" : selectedCard?.state === "calculated" || selectedCard?.state === "exported" ? "error" : "pending",
      detail: prodCalc
        ? renderDaySummary(prodCalc)
        : selectedCard
          ? `Noch nicht berechnet (state=${selectedCard.state})`
          : "(wartet)",
    },
    {
      num: 5,
      title: "Biweekly-Mapping (ISO-8601)",
      description:
        "KW 1 = erste Woche mit Donnerstag. Konvention: ungerade KW → week_a, gerade KW → week_b.",
      status: !schedule ? "pending" : prodCalc ? "done" : "pending",
      detail: !schedule
        ? "(wartet auf Schedule)"
        : schedule.pattern === "biweekly"
          ? renderBiweeklyDetail(prodCalc)
          : "pattern=weekly — kein Mapping nötig",
      optional: schedule?.pattern !== "biweekly",
    },
    {
      num: 6,
      title: "Wochen-Aggregation",
      description: "Pro ISO-KW: Σ ist, Σ soll, Σ Δ. Master-System macht den Rest.",
      status: prodCalc?.weeks?.length ? "done" : "pending",
      detail: prodCalc?.weeks ? renderWeeks(prodCalc) : "(wartet)",
    },
    {
      num: 7,
      title: "Status-Aggregat (Master-Übergabe)",
      description: "Anzahl K/U/F/ZA/P + konsumierte ZA-Stunden. Vortrag-Saldo trackt RZL.",
      status: prodCalc ? "done" : "pending",
      detail: prodCalc ? renderStatusAggregat(prodCalc) : "(wartet)",
    },
    {
      num: 8,
      title: "Monats-Total + Hinweise",
      description: "Summen + Hinweis-Count zur Validierung durch Birgit.",
      status: prodCalc ? "done" : "pending",
      detail: prodCalc ? renderTotals(prodCalc) : "(wartet)",
    },
    {
      num: 9,
      title: "RZL-Export-Preview",
      description: "Phase 6 nutzt diese data.calculation zum Excel-Export.",
      status: selectedCard?.state === "exported" ? "done" : "pending",
      detail: selectedCard?.state === "exported"
        ? "✓ Exportiert"
        : "wird beim Phase-6-Export gebaut",
      optional: true,
    },
  ];

  return (
    <div>
      <div className="toolbar" style={{ marginBottom: 16 }}>
        <label className="field-inline">
          <span>Karte</span>
          <select value={cardId} onChange={(e) => setCardId(e.target.value)}>
            <option value="">— wählen —</option>
            {cards.filter(c =>
              ["ocr_complete", "validated", "calculated", "exported"].includes(c.state as any),
            ).map(c => {
              const emp = employees.find(e => e.id === c.employee_id);
              const label = emp ? `${emp.last_name}, ${emp.first_name}` : "(unzugeordnet)";
              return (
                <option key={c.id} value={c.id}>
                  {c.month} · {label} · {c.state} · {c.id.slice(0, 8)}
                </option>
              );
            })}
          </select>
        </label>
        {busy && <span className="muted">lade…</span>}
        {loadErr && <span style={{ color: "#dc2626" }}>{loadErr}</span>}
      </div>

      <div style={{
        padding: 16, marginBottom: 12, borderRadius: 6,
        background: "#eff6ff", border: "1px solid #93c5fd",
      }}>
        <strong>Abrechnungs-Pipeline · Glass-Box (Production-Daten)</strong>
        <p style={{ margin: "8px 0 0 0", fontSize: 13, color: "#444" }}>
          Werte aus <code>card.data.calculation</code> — produziert von{" "}
          <code>worker/calc-abrechnung.ts</code>. Single-Source: Werkstatt rechnet
          NICHT selbst, sondern zeigt was Production gerechnet hat.
        </p>
      </div>

      <PipelineStepList steps={selectedCard ? steps.map(s => ({ ...s, uuid: stepUuid(selectedCard.id, "berechnung", s.num) })) : steps} />

      {prodCalc && (
        <div style={{ marginTop: 24 }}>
          <h3>Tages-Tabelle (Production)</h3>
          <DayTable days={prodCalc.days ?? []} />
        </div>
      )}
    </div>
  );
}

// ─── Renderers ────────────────────────────────────────────────────────────

function scheduleDetail(s: any): string {
  if (!s) return "—";
  const pattern = s.pattern ?? "weekly";
  const a = s.week_a ?? {};
  const aStr = `Mo ${a.mo ?? 0} · Di ${a.di ?? 0} · Mi ${a.mi ?? 0} · Do ${a.do ?? 0} · Fr ${a.fr ?? 0} · Sa ${a.sa ?? 0}`;
  if (pattern === "biweekly" && s.week_b) {
    const b = s.week_b;
    const bStr = `Mo ${b.mo ?? 0} · Di ${b.di ?? 0} · Mi ${b.mi ?? 0} · Do ${b.do ?? 0} · Fr ${b.fr ?? 0} · Sa ${b.sa ?? 0}`;
    return `${pattern} · A: ${aStr} (Σ ${s.soll_h_woche_a}h) · B: ${bStr} (Σ ${s.soll_h_woche_b}h)`;
  }
  return `${pattern} · ${aStr} · Σ ${s.soll_h_woche_a ?? "?"}h/Woche${s.notes ? " · " + s.notes : ""}`;
}

function renderDaySummary(r: MonthResult): string {
  const filled = (r.days ?? []).filter(d => d.ist_h > 0 || d.status).length;
  const hints = (r.days ?? []).reduce((s, d) => s + d.hints.length, 0);
  return `${filled}/${r.days?.length ?? 0} Tage befüllt · Σ ist ${r.totals.stunden_ist}h · Σ soll ${r.totals.stunden_soll}h · ${hints} Hinweise`;
}

function renderBiweeklyDetail(r: MonthResult | null): string {
  if (!r) return "(wartet)";
  const byWeek = new Map<number, { woche: "a" | "b"; days: number }>();
  for (const d of r.days ?? []) {
    const entry = byWeek.get(d.iso_week) ?? { woche: d.woche, days: 0 };
    entry.days++;
    byWeek.set(d.iso_week, entry);
  }
  return [...byWeek.entries()].sort(([a], [b]) => a - b)
    .map(([wk, { woche, days }]) => `KW${wk}=${woche} (${days}d)`).join(" · ");
}

function renderWeeks(r: MonthResult): string {
  return (r.weeks ?? []).map(w => `KW${w.iso_week}: ist ${w.ist_h_sum}h soll ${w.soll_h_sum}h Δ ${formatSigned(w.diff_h_sum)}h`).join(" · ");
}

function renderStatusAggregat(r: MonthResult): string {
  const t = r.totals;
  return `K ${t.tage_K}d · U ${t.tage_U}d · F ${t.tage_F}d · ZA ${t.tage_ZA}d (${t.stunden_ZA_konsumiert}h) · P ${t.tage_P}d`;
}

function renderTotals(r: MonthResult): string {
  const t = r.totals;
  return `Σ ist ${t.stunden_ist}h · Σ soll ${t.stunden_soll}h · Δ ${formatSigned(t.diff_h)}h · ${t.hints_count} Hinweise`;
}

function formatSigned(n: number): string {
  return n > 0 ? `+${n}` : String(n);
}

function DayTable(props: { days: DayResult[] }) {
  return (
    <table className="day-table" style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ textAlign: "left", borderBottom: "1px solid #ddd" }}>
          <th>Tag</th><th>Datum</th><th>KW</th><th>W</th><th>Status</th>
          <th style={{ textAlign: "right" }}>Ist</th>
          <th style={{ textAlign: "right" }}>Soll</th>
          <th style={{ textAlign: "right" }}>Δ</th>
          <th>Hinweise</th>
        </tr>
      </thead>
      <tbody>
        {props.days.map(d => {
          const hintsRed = d.hints.filter(h => h.level === "red").length;
          const hintsYellow = d.hints.filter(h => h.level === "yellow").length;
          const bgRed = hintsRed > 0 ? "rgba(239,68,68,0.08)" : undefined;
          const bgYellow = !bgRed && hintsYellow > 0 ? "rgba(245,158,11,0.08)" : undefined;
          return (
            <tr key={d.day}
                style={{ borderBottom: "1px solid #eee", background: bgRed ?? bgYellow }}
                title={d.hints.map(h => h.text).join("\n")}>
              <td>{d.day}</td>
              <td>{d.date}</td>
              <td>KW{d.iso_week}</td>
              <td>{d.woche.toUpperCase()}</td>
              <td>{d.status ?? ""}</td>
              <td style={{ textAlign: "right" }}>{d.ist_h.toFixed(2)}</td>
              <td style={{ textAlign: "right" }}>{d.soll_h.toFixed(2)}</td>
              <td style={{ textAlign: "right" }}>{formatSigned(+d.diff_h.toFixed(2))}</td>
              <td>
                {hintsRed > 0 && <span style={{ color: "#dc2626", marginRight: 6 }}>🔴 {hintsRed}</span>}
                {hintsYellow > 0 && <span style={{ color: "#d97706" }}>🟡 {hintsYellow}</span>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
